/**
 * Package Search Dialog - Enhanced with categorized package selection
 * Search and select packages from PyPI/conda with organized categories
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Search, Package, ChevronDown, ChevronRight, Check } from 'lucide-react';

interface PackageInfo {
  name: string;
  version: string;
  source: 'pypi' | 'conda-forge';
  summary: string;
  supportsGpu: boolean;
  architectureSupport: string[];
  category?: string;
  size?: string;
  popular?: boolean;
}

interface PackageSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onAddPackage: (packageName: string) => void; // Should toggle: add if not in existingPackages, remove if present
  onBulkSelect?: (packages: string[]) => void;
  architecture: 'x86_64' | 'arm64';
  pythonVersion: string;
  gpuOnly: boolean;
  existingPackages: string[];
}

interface PackageCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  packages: PackageInfo[];
}

const CATEGORIZED_PACKAGES: PackageCategory[] = [
  {
    id: 'ml_frameworks',
    name: 'ML Frameworks',
    description: 'Machine learning and deep learning frameworks',
    icon: '🧠',
    packages: [
      { name: 'tensorflow', version: '2.15.0', source: 'pypi', summary: 'Open-source machine learning framework', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'ml_frameworks', size: '500MB', popular: true },
      { name: 'torch', version: '2.1.0', source: 'pypi', summary: 'Deep learning framework with dynamic computation graphs', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'ml_frameworks', size: '800MB', popular: true },
      { name: 'scikit-learn', version: '1.3.0', source: 'pypi', summary: 'Machine learning library for Python', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'ml_frameworks', size: '50MB', popular: true },
      { name: 'keras', version: '2.15.0', source: 'pypi', summary: 'High-level neural networks API', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'ml_frameworks', size: '200MB' },
      { name: 'xgboost', version: '2.0.0', source: 'pypi', summary: 'Gradient boosting framework', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'ml_frameworks', size: '15MB', popular: true },
      { name: 'lightgbm', version: '4.1.0', source: 'pypi', summary: 'Fast gradient boosting framework', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'ml_frameworks', size: '10MB', popular: true },
    ]
  },
  {
    id: 'data_science',
    name: 'Data Science',
    description: 'Data analysis, manipulation, and scientific computing',
    icon: '📊',
    packages: [
      { name: 'pandas', version: '2.1.0', source: 'pypi', summary: 'Data manipulation and analysis library', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'data_science', size: '40MB', popular: true },
      { name: 'numpy', version: '1.25.0', source: 'pypi', summary: 'Fundamental package for scientific computing', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'data_science', size: '15MB', popular: true },
      { name: 'scipy', version: '1.11.0', source: 'pypi', summary: 'Scientific computing library', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'data_science', size: '25MB', popular: true },
      { name: 'dask', version: '2023.9.0', source: 'pypi', summary: 'Parallel computing library', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'data_science', size: '100MB' },
      { name: 'polars', version: '0.19.0', source: 'pypi', summary: 'Fast DataFrame library', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'data_science', size: '30MB' },
      { name: 'jupyter', version: '1.0.0', source: 'pypi', summary: 'Interactive computing environment', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'data_science', size: '50MB', popular: true },
    ]
  },
  {
    id: 'visualization',
    name: 'Visualization',
    description: 'Data visualization and plotting libraries',
    icon: '📈',
    packages: [
      { name: 'matplotlib', version: '3.7.0', source: 'pypi', summary: 'Comprehensive plotting library', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'visualization', size: '35MB', popular: true },
      { name: 'seaborn', version: '0.12.0', source: 'pypi', summary: 'Statistical data visualization', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'visualization', size: '20MB', popular: true },
      { name: 'plotly', version: '5.17.0', source: 'pypi', summary: 'Interactive plotting library', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'visualization', size: '60MB' },
      { name: 'bokeh', version: '3.2.0', source: 'pypi', summary: 'Interactive visualization library', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'visualization', size: '40MB' },
    ]
  },
  {
    id: 'gpu_tools',
    name: 'GPU Tools',
    description: 'CUDA, GPU acceleration, and monitoring tools',
    icon: '⚡',
    packages: [
      { name: 'cupy', version: '12.2.0', source: 'pypi', summary: 'NumPy-compatible array library for GPU', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_tools', size: '300MB', popular: true },
      { name: 'numba', version: '0.58.0', source: 'pypi', summary: 'JIT compiler for Python and NumPy', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'gpu_tools', size: '50MB', popular: true },
      { name: 'nvidia-ml-py', version: '12.535.0', source: 'pypi', summary: 'Python bindings for NVIDIA Management Library', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_tools', size: '5MB' },
      { name: 'gpustat', version: '1.1.0', source: 'pypi', summary: 'GPU utilization monitoring tool', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_tools', size: '1MB' },
    ]
  },
  {
    id: 'gpu_computing',
    name: 'GPU Computing & HPC',
    description: 'RAPIDS ecosystem, distributed computing, and high-performance parallel processing',
    icon: '🚀',
    packages: [
      { name: 'cudf', version: '23.10.0', source: 'pypi', summary: 'GPU DataFrame library - pandas on CUDA', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_computing', size: '400MB', popular: true },
      { name: 'cuml', version: '23.10.0', source: 'pypi', summary: 'GPU-accelerated machine learning library', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_computing', size: '350MB', popular: true },
      { name: 'cugraph', version: '23.10.0', source: 'pypi', summary: 'GPU-accelerated graph analytics', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_computing', size: '200MB', popular: true },
      { name: 'cusignal', version: '23.10.0', source: 'pypi', summary: 'GPU-accelerated signal processing', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_computing', size: '100MB' },
      { name: 'cupy-cuda12x', version: '12.3.0', source: 'pypi', summary: 'CuPy for CUDA 12.x (NumPy on GPU)', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_computing', size: '300MB', popular: true },
      { name: 'dask', version: '2023.11.0', source: 'pypi', summary: 'Parallel computing with task scheduling', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'gpu_computing', size: '100MB', popular: true },
      { name: 'dask-cuda', version: '23.10.0', source: 'pypi', summary: 'GPU cluster setup and utilities for Dask', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_computing', size: '50MB' },
      { name: 'rapids', version: '23.10.0', source: 'pypi', summary: 'RAPIDS meta-package for GPU data science', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_computing', size: '1GB', popular: true },
      { name: 'numba-cuda', version: '0.58.0', source: 'pypi', summary: 'CUDA support for Numba JIT compiler', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_computing', size: '60MB' },
      { name: 'pycuda', version: '2022.2.2', source: 'pypi', summary: 'Python interface to CUDA parallel computing', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_computing', size: '80MB' },
      { name: 'ray', version: '2.8.0', source: 'pypi', summary: 'Distributed computing framework for ML', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'gpu_computing', size: '200MB', popular: true },
      { name: 'modin', version: '0.25.0', source: 'pypi', summary: 'Parallel pandas with Ray/Dask backend', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'gpu_computing', size: '70MB' },
      { name: 'horovod', version: '0.28.1', source: 'pypi', summary: 'Distributed deep learning training framework', supportsGpu: true, architectureSupport: ['x86_64'], category: 'gpu_computing', size: '150MB' },
      { name: 'jax', version: '0.4.20', source: 'pypi', summary: 'Composable transformations of Python+NumPy', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'gpu_computing', size: '400MB', popular: true },
    ]
  },
  {
    id: 'quant_finance',
    name: 'Quantitative Finance',
    description: 'Backtesting, portfolio optimization, risk analytics, and financial modeling',
    icon: '📈',
    packages: [
      { name: 'vectorbt', version: '0.26.1', source: 'pypi', summary: 'High-speed vectorized backtesting (10-100x faster than zipline)', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '50MB', popular: true },
      { name: 'backtrader', version: '1.9.78', source: 'pypi', summary: 'Feature-rich backtesting with live trading support', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '30MB', popular: true },
      { name: 'backtesting', version: '0.3.3', source: 'pypi', summary: 'Simple modern backtesting library', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '10MB' },
      { name: 'bt', version: '0.2.9', source: 'pypi', summary: 'Flexible algorithmic backtesting framework', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '15MB' },
      { name: 'riskfolio-lib', version: '6.1.0', source: 'pypi', summary: 'Portfolio optimization with 24 risk measures', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '25MB', popular: true },
      { name: 'pyportfolioopt', version: '1.5.5', source: 'pypi', summary: 'Efficient frontier, Black-Litterman, risk parity', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '20MB', popular: true },
      { name: 'pyfolio-reloaded', version: '0.9.5', source: 'pypi', summary: 'Portfolio analytics (maintained fork, Python 3.12 compatible)', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '30MB' },
      { name: 'empyrical-reloaded', version: '0.5.9', source: 'pypi', summary: 'Risk and performance metrics (maintained fork)', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '10MB' },
      { name: 'quantlib', version: '1.32', source: 'pypi', summary: 'Derivatives pricing and risk management', supportsGpu: false, architectureSupport: ['x86_64'], category: 'quant_finance', size: '40MB' },
      { name: 'yfinance', version: '0.2.32', source: 'pypi', summary: 'Yahoo Finance market data downloader', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '15MB', popular: true },
      { name: 'pandas-ta', version: '0.3.14', source: 'pypi', summary: '130+ technical indicators for pandas', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '20MB', popular: true },
      { name: 'ta-lib', version: '0.4.28', source: 'pypi', summary: 'Industry standard 200+ technical analysis functions', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '5MB', popular: true },
      { name: 'ta', version: '0.11.0', source: 'pypi', summary: '40+ technical indicators with simple API', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '8MB' },
      { name: 'arch', version: '6.3.0', source: 'pypi', summary: 'GARCH models and volatility forecasting', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '12MB' },
      { name: 'statsmodels', version: '0.14.1', source: 'pypi', summary: 'Statistical models and econometric analysis', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '35MB', popular: true },
      { name: 'pykalman', version: '0.9.7', source: 'pypi', summary: 'Kalman filters for time series', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '5MB' },
      { name: 'tsfresh', version: '0.20.2', source: 'pypi', summary: 'Time series feature extraction', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '30MB' },
      { name: 'pytorch-forecasting', version: '1.0.0', source: 'pypi', summary: 'Time series forecasting with PyTorch', supportsGpu: true, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '80MB' },
      { name: 'prophet', version: '1.1.5', source: 'pypi', summary: 'Forecasting time series data (by Meta)', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '25MB' },
      { name: 'mplfinance', version: '0.12.10', source: 'pypi', summary: 'Financial market data visualization', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'quant_finance', size: '15MB' },
    ]
  },
  {
    id: 'cloud_storage',
    name: 'Cloud Storage',
    description: 'Cloud storage and data connectors',
    icon: '☁️',
    packages: [
      { name: 'boto3', version: '1.29.0', source: 'pypi', summary: 'AWS SDK for Python', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'cloud_storage', size: '20MB', popular: true },
      { name: 'google-cloud-storage', version: '2.10.0', source: 'pypi', summary: 'Google Cloud Storage client', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'cloud_storage', size: '30MB' },
      { name: 'azure-storage-blob', version: '12.19.0', source: 'pypi', summary: 'Azure Blob Storage client', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'cloud_storage', size: '25MB' },
      { name: 's3fs', version: '2023.9.0', source: 'pypi', summary: 'Pythonic file interface to S3', supportsGpu: false, architectureSupport: ['x86_64', 'arm64'], category: 'cloud_storage', size: '10MB' },
    ]
  }
];

// Legacy popular packages for backwards compatibility
const POPULAR_PACKAGES = [
  'tensorflow', 'torch', 'scikit-learn', 'pandas', 'numpy',
  'matplotlib', 'seaborn', 'jupyter', 'xgboost', 'lightgbm',
  'opencv-python', 'pillow', 'requests', 'flask', 'fastapi'
];

export default function PackageSearchDialog({
  open,
  onClose,
  onAddPackage,
  onBulkSelect,
  architecture,
  pythonVersion,
  gpuOnly,
  existingPackages
}: PackageSearchDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['ml_frameworks', 'quant_finance', 'gpu_tools', 'gpu_computing']));
  const [showPopularOnly, setShowPopularOnly] = useState(false);

  // Filter categorized packages based on current criteria
  const filteredCategorizedPackages = useMemo(() => {
    let allPackages = CATEGORIZED_PACKAGES.flatMap(cat => cat.packages);

    // Architecture filter
    allPackages = allPackages.filter(pkg =>
      pkg.architectureSupport.includes(architecture)
    );

    // GPU filter
    if (gpuOnly) {
      allPackages = allPackages.filter(pkg => pkg.supportsGpu);
    }

    // Category filter
    if (selectedCategory !== 'all') {
      allPackages = allPackages.filter(pkg => pkg.category === selectedCategory);
    }

    // Popular filter
    if (showPopularOnly) {
      allPackages = allPackages.filter(pkg => pkg.popular);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      allPackages = allPackages.filter(pkg =>
        pkg.name.toLowerCase().includes(query) ||
        pkg.summary?.toLowerCase().includes(query)
      );
    }

    return allPackages;
  }, [searchQuery, selectedCategory, showPopularOnly, architecture, gpuOnly]);

  const selectedCount = existingPackages.length;
  const totalSize = useMemo(() => {
    const selectedPkgs = CATEGORIZED_PACKAGES.flatMap(cat => cat.packages)
      .filter(pkg => existingPackages.includes(pkg.name));
    return selectedPkgs.reduce((total, pkg) => {
      const size = pkg.size?.replace(/[^\d]/g, '') || '0';
      return total + parseInt(size);
    }, 0);
  }, [existingPackages]);

  const handleAddPackage = (packageName: string) => {
    onAddPackage(packageName);
    setSearchQuery('');
  };

  const handleBulkSelect = (packages: string[]) => {
    if (onBulkSelect) {
      onBulkSelect(packages);
    }
  };

  const isPackageAdded = (packageName: string) => {
    return existingPackages.includes(packageName);
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const selectCategoryPackages = (categoryId: string) => {
    const category = CATEGORIZED_PACKAGES.find(cat => cat.id === categoryId);
    if (category) {
      const compatiblePackages = category.packages
        .filter(pkg => pkg.architectureSupport.includes(architecture))
        .filter(pkg => !gpuOnly || pkg.supportsGpu)
        .map(pkg => pkg.name);
      handleBulkSelect(compatiblePackages);
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [open]);

  if (!open) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-5xl w-full h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-white">Add Python Package</h3>
            <p className="text-sm text-gray-400">
              Search for packages compatible with {architecture} architecture
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-300 p-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-700 flex-shrink-0">
          <nav className="flex -mb-px">
            <button
              onClick={() => setSelectedTab(0)}
              className={`py-3 px-6 border-b-2 font-medium text-sm ${
                selectedTab === 0
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Package Categories
            </button>
            <button
              onClick={() => setSelectedTab(1)}
              className={`py-3 px-6 border-b-2 font-medium text-sm ${
                selectedTab === 1
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Popular Packages
            </button>
          </nav>
        </div>

        {/* Content - Explicit height constraint for proper scrolling */}
        <div className="flex-1 overflow-hidden">
          {/* Package Categories Tab */}
          {selectedTab === 0 && (
            <div className="h-full flex">
              {/* Categories Sidebar */}
              <div className="w-64 border-r border-gray-700 flex flex-col flex-shrink-0 h-full">
                <div className="flex-shrink-0 p-3 border-b border-gray-700">
                  <div className="mb-3">
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2.5 py-1.5 text-sm text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="all">All Categories</option>
                      {CATEGORIZED_PACKAGES.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={showPopularOnly}
                      onChange={(e) => setShowPopularOnly(e.target.checked)}
                      className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-800"
                    />
                    Popular only
                  </label>
                </div>

                <div
                  className="flex-1 overflow-y-scroll min-h-0"
                  data-testid="category-sidebar-scroll"
                >
                {CATEGORIZED_PACKAGES.map(category => {
                  const isExpanded = expandedCategories.has(category.id);
                  const categoryPackages = category.packages.filter(pkg =>
                    pkg.architectureSupport.includes(architecture) &&
                    (!gpuOnly || pkg.supportsGpu) &&
                    (selectedCategory === 'all' || selectedCategory === category.id)
                  );
                  const visiblePackages = showPopularOnly
                    ? categoryPackages.filter(pkg => pkg.popular)
                    : categoryPackages;

                  if (selectedCategory !== 'all' && selectedCategory !== category.id) {
                    return null;
                  }

                  return (
                    <div key={category.id} className="border-b border-gray-700 last:border-b-0">
                      <div
                        className="p-3 hover:bg-gray-700/50 cursor-pointer"
                        onClick={() => toggleCategory(category.id)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-base flex-shrink-0">{category.icon}</span>
                            <h3 className="text-sm font-medium text-white truncate">{category.name}</h3>
                          </div>
                          <div className="flex-shrink-0 ml-2">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between ml-6">
                          <span className="text-xs text-gray-400">{visiblePackages.length} packages</span>
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                selectCategoryPackages(category.id);
                              }}
                              className="text-xs px-2 py-0.5 bg-blue-600 hover:bg-blue-700 rounded text-white transition-colors"
                            >
                              Select All
                            </button>
                          )}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="pb-1">
                          {visiblePackages.map(pkg => (
                            <div
                              key={pkg.name}
                              className="px-4 py-1.5 hover:bg-gray-700/30 cursor-pointer flex items-center gap-2"
                              onClick={() => handleAddPackage(pkg.name)}
                              title={isPackageAdded(pkg.name) ? "Click to unselect" : "Click to select"}
                            >
                              <div className="flex-shrink-0">
                                {isPackageAdded(pkg.name) ? (
                                  <div className="w-4 h-4 bg-blue-500 rounded flex items-center justify-center">
                                    <Check className="h-3 w-3 text-white" />
                                  </div>
                                ) : (
                                  <div className="w-4 h-4 border border-gray-500 rounded" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-sm font-medium text-white truncate">{pkg.name}</span>
                                  <div className="flex items-center gap-1.5 flex-shrink-0 text-xs text-gray-400">
                                    {pkg.popular && (
                                      <span className="px-1 py-0.5 bg-orange-500/20 text-orange-400 rounded">★</span>
                                    )}
                                    {pkg.supportsGpu && (
                                      <span className="px-1 py-0.5 bg-purple-500/20 text-purple-400 rounded">GPU</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                  <span>v{pkg.version}</span>
                                  <span>•</span>
                                  <span>{pkg.size}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>

              {/* Selected Packages Panel */}
              <div className="flex-1 flex flex-col overflow-hidden h-full">
                <div className="flex-shrink-0 p-4 border-b border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-white">
                      Selected Packages ({selectedCount})
                    </h3>
                    {selectedCount > 0 && (
                      <button
                        onClick={() => {
                          existingPackages.forEach(() => {
                            // Clear all packages by removing them individually
                          });
                        }}
                        className="text-xs text-red-400 hover:text-red-300 transition-colors"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">
                    Total size: ~{Math.round(totalSize/10)/100}GB
                  </div>
                  <div className="relative mt-3">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search packages..."
                      className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 p-4">

                {searchQuery && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-white mb-2">
                      Search Results ({filteredCategorizedPackages.length})
                    </h4>
                    <div className="space-y-2">
                      {filteredCategorizedPackages.map(pkg => (
                        <div
                          key={pkg.name}
                          className="p-2.5 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 cursor-pointer border border-gray-600/50 flex items-center gap-2"
                          onClick={() => handleAddPackage(pkg.name)}
                          title={isPackageAdded(pkg.name) ? "Click to unselect" : "Click to select"}
                        >
                          <div className="flex-shrink-0">
                            {isPackageAdded(pkg.name) ? (
                              <div className="w-4 h-4 bg-blue-500 rounded flex items-center justify-center">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                            ) : (
                              <div className="w-4 h-4 border border-gray-500 rounded" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium text-white truncate">{pkg.name}</span>
                              <div className="flex items-center gap-1.5 flex-shrink-0 text-xs text-gray-400">
                                <span>v{pkg.version}</span>
                                <span>•</span>
                                <span>{pkg.size}</span>
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 truncate">{pkg.summary}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!searchQuery && selectedCount === 0 && (
                  <div className="text-center py-12">
                    <div className="text-gray-500 mb-2">
                      <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    </div>
                    <h4 className="text-sm font-medium text-gray-400 mb-2">No packages selected</h4>
                    <p className="text-xs text-gray-500 max-w-xs mx-auto">
                      Expand categories on the left to browse and select packages, or use the search above to find specific packages.
                    </p>
                  </div>
                )}

                {!searchQuery && selectedCount > 0 && (
                  <div className="space-y-1.5">
                    {existingPackages.map((pkgName) => {
                      const pkg = CATEGORIZED_PACKAGES.flatMap(cat => cat.packages).find(p => p.name === pkgName);
                      if (!pkg) return null;

                      return (
                        <div
                          key={pkgName}
                          onClick={() => handleAddPackage(pkg.name)}
                          className="p-2 bg-gray-700/30 rounded-lg border border-gray-600/50 hover:border-gray-500 transition-colors cursor-pointer"
                          title="Click to unselect"
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0">
                              <div className="w-4 h-4 bg-blue-500 rounded flex items-center justify-center hover:bg-blue-600 transition-colors">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium text-white truncate">{pkg.name}</span>
                                <div className="flex items-center gap-1.5 flex-shrink-0 text-xs text-gray-400">
                                  {pkg.popular && (
                                    <span className="px-1 py-0.5 bg-orange-500/20 text-orange-400 rounded">★</span>
                                  )}
                                  {pkg.supportsGpu && (
                                    <span className="px-1 py-0.5 bg-purple-500/20 text-purple-400 rounded">GPU</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                <span>v{pkg.version}</span>
                                <span>•</span>
                                <span>{pkg.size}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
            </div>
          )}

          {/* Popular Packages Tab */}
          {selectedTab === 1 && (
            <div className="h-full overflow-y-auto p-4 space-y-3">
              <h4 className="text-sm font-medium text-white">Popular Machine Learning Packages</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {POPULAR_PACKAGES.map((pkg) => {
                  const isAdded = isPackageAdded(pkg);
                  return (
                    <button
                      key={pkg}
                      onClick={() => handleAddPackage(pkg)}
                      className={`p-2 text-left rounded-lg border transition-colors ${
                        isAdded
                          ? 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-gray-700/50 border-gray-600 text-white hover:bg-gray-700 hover:border-blue-500'
                      }`}
                      title={isAdded ? "Click to unselect" : "Click to select"}
                    >
                      <div className="text-sm font-medium truncate">{pkg}</div>
                      {isAdded && <div className="text-xs text-gray-500">✓ Added</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex flex-col gap-0.5">
            <div className="text-sm font-medium text-white">
              {selectedCount} {selectedCount === 1 ? 'package' : 'packages'} selected
            </div>
            <div className="text-xs text-gray-400">
              Total size: ~{(Math.round(totalSize/100)/10).toFixed(1)} GB
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}